/**
 * 🎨 APP LAYOUT COMPONENT
 * 
 * Extrage logica de layout pentru utilizatorii autentificați din App.tsx
 * pentru a transforma App.tsx într-un orchestrator curat.
 * 
 * Responsabilități:
 * - Grid layout cu sidebar și main content
 * - HybridNavigation positioning
 * - LunarGrid page special handling
 * - Mobile responsive padding și navigation spacing
 * 
 * Folosește doar CVA variants care funcționează (flexLayout, container)
 * NU folosește gridTemplate cu CSS Grid Template Areas
 */

import React from 'react';
import { useLocation, Outlet } from "react-router";
import { cn } from '../../styles/cva-v2';
import HybridNavigation from '../primitives/HybridNavigation';
import PocketMoneyIndicator from '../ui/PocketMoneyIndicator';
import { BuzunarLogo } from '../shared/BuzunarLogo';

interface AppLayoutProps {
  /** Logout handler pentru HybridNavigation */
  onLogout: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Layout component pentru utilizatorii autentificați
 * Gestionează structura grid cu sidebar și main content
 */
const AppLayout = ({
  onLogout,
  className
}: AppLayoutProps) => {
  const location = useLocation();
  
  // Check if we're on LunarGrid page - needs special overflow handling
  const isLunarGridPage = location.pathname === '/lunar-grid';

  return (
    <div className={cn(
      // Full height layout cu header și content
      'min-h-screen flex flex-col',
      className
    )}>
      
      {/* 🔧 WORKAROUND A: Disable header doar pe LunarGrid pentru eliminarea sticky conflict */}
      {!isLunarGridPage && (
        <header className="sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BuzunarLogo className="w-8 h-8" variant="icon" />
            <h1 className="text-xl font-bold text-copper-600 dark:text-copper-400">
              Buzunarul
            </h1>
          </div>
          
          <PocketMoneyIndicator 
            compact={false}
            showBreakdown={false}
            className="relative"
          />
          
          <div className="w-[180px]" />
        </div>
        </header>
      )}

      {/* Content area cu navigation și main */}
      <div className={cn(
        'flex-1 lg:grid lg:grid-cols-[auto_1fr]'
      )}>
        
        {/* Navigation Sidebar/BottomBar */}
        <HybridNavigation 
          onLogout={onLogout}
          showLogout={true}
          data-testid="main-navigation"
        />
        
        {/* Main Content Area */}
        <main className={cn(
          // 🔧 TIER 3: Eliminat complet toate overflow restrictions pentru LunarGrid
          // Permite scroll natural în toate direcțiile
          
          // Mobile navigation spacing - bottom padding pentru BottomTabBar
          'pb-20 lg:pb-0',
          
          // Content padding - doar pentru pagini normale, nu LunarGrid
          isLunarGridPage ? '':'p-4 lg:p-6',
          
          // 🔧 TIER 3: Permite overflow explicit pe LunarGrid
          isLunarGridPage ? 'overflow-visible' : ''
        )}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppLayout; 